import { useEffect, useState, useCallback, useRef } from 'react'
import { api } from '../services/api'
import useAuthStore from '../stores/authStore'
import { buildApiUrl } from '../config/apiBaseUrl'

export const useNotifications = () => {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isConnected, setIsConnected] = useState(false)
  const { accessToken } = useAuthStore()

  // Load initial notifications
  const loadNotifications = useCallback(async () => {
    try {
      const response = await api.get('/notifications')
      setNotifications(response.data.data)
      
      const countResponse = await api.get('/notifications/count')
      setUnreadCount(countResponse.data.data.count)
    } catch (error) {
      console.error('Error loading notifications:', error)
    }
  }, [])

  // Setup SSE connection con reconexión automática y backoff.
  // (El navegador deja la conexión en CLOSED ante algunos errores de proxy;
  //  reconectamos manualmente y sin ensuciar la consola con errores.)
  const reconnectRef = useRef(() => {})

  useEffect(() => {
    if (!accessToken) return

    let eventSource = null
    let reconnectTimer = null
    let retries = 0
    let cancelled = false

    const connect = () => {
      if (cancelled) return
      const streamUrl = `${buildApiUrl('/notifications/stream')}?access_token=${encodeURIComponent(accessToken)}`
      eventSource = new EventSource(streamUrl)

      eventSource.onopen = () => {
        retries = 0
        setIsConnected(true)
      }

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'notification') {
            setNotifications(prev => [data.data, ...prev])
            setUnreadCount(prev => prev + 1)
          } else if (data.type === 'unread_count') {
            setUnreadCount(data.count)
          }
        } catch (error) {
          console.debug('SSE: mensaje no parseable', error)
        }
      }

      eventSource.onerror = () => {
        setIsConnected(false)
        // Si el navegador cerró la conexión, reintentar con backoff (2s→30s).
        if (eventSource && eventSource.readyState === EventSource.CLOSED) {
          eventSource.close()
          if (!cancelled) {
            const delay = Math.min(30000, 2000 * 2 ** retries)
            retries += 1
            reconnectTimer = setTimeout(connect, delay)
          }
        }
      }
    }

    connect()
    loadNotifications()

    // Reconexión manual (botón "Reintentar" en el dropdown): corta el backoff
    // en curso y reintenta ya mismo, en vez de esperar hasta 30s.
    reconnectRef.current = () => {
      if (reconnectTimer) clearTimeout(reconnectTimer)
      retries = 0
      if (eventSource) eventSource.close()
      connect()
      loadNotifications()
    }

    return () => {
      cancelled = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (eventSource) eventSource.close()
    }
  }, [accessToken, loadNotifications])

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`)
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, leida: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }, [])

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await api.post('/notifications/read-all')
      setNotifications(prev => prev.map(n => ({ ...n, leida: true })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  }, [])

  return {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    refresh: loadNotifications,
    reconnect: useCallback(() => reconnectRef.current(), [])
  }
}
